import { useCallback, useState } from "react"
import { Platform, StyleSheet, TextStyle, useWindowDimensions, View, ViewStyle } from "react-native"

import type { LayoutChangeEvent } from "react-native"
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { usePostHog } from "posthog-react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { Alert } from "src/components/Alert"
import { CloseButton } from "src/components/CloseButton"
import { Text } from "src/components/Ignite"
import { Loading } from "src/components/Loading"
import { SelectSponsor } from "src/components/SelectSponsor"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCloseCurrentConversation, useCurrentConversation } from "src/queries/useConversation"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { SponsorInfo } from "src/services/api/api.types"
import { ThemedStyle } from "src/theme"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"
import { backOrToRoot } from "src/utils/routerHelpers"

/**
 * ChangeChannelScreen - A full-screen channel/sponsor switcher that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Channel/Sponsor selection via SelectSponsor component
 */
function normalizeParam(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== "string") return undefined
  const t = raw.trim()
  return t.length > 0 ? t : undefined
}

export default function ChangeChannelScreen() {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()
  const posthog = usePostHog()
  const { clearPendingChannelId } = useChannelLink()
  const { isSessionDeactivated } = useAuth()
  const { autoSwitchChannelId: autoSwitchChannelIdParam } = useLocalSearchParams<{
    autoSwitchChannelId?: string | string[]
  }>()
  const autoSwitchChannelId = normalizeParam(autoSwitchChannelIdParam)
  const { data: deeplinkChannelInfo } = useUnauthChannelInfoQuery(autoSwitchChannelId ?? null)
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const { data: currentConversation } = useCurrentConversation()
  const closeCurrentConversationMutation = useCloseCurrentConversation()

  // State to hide avatar during channel switch
  const [hideAvatar, setHideAvatar] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(60)

  const shouldCloseConversation = !!currentConversation

  const handleHeaderLayout = useCallback((e: LayoutChangeEvent) => {
    setHeaderHeight(e.nativeEvent.layout.height)
  }, [])

  const handleClose = useCallback(() => {
    backOrToRoot()
  }, [])

  // Callbacks for SelectSponsor
  const handleSwitchStart = useCallback(() => {
    setHideAvatar(true)
  }, [])

  const handleSwitchError = useCallback(() => {
    setHideAvatar(false)
    if (autoSwitchChannelId) {
      clearPendingChannelId()
    }
  }, [autoSwitchChannelId, clearPendingChannelId])

  const handleCloseConversation = useCallback(async () => {
    try {
      await closeCurrentConversationMutation.mutateAsync()
      return true
    } catch {
      return false
    }
  }, [closeCurrentConversationMutation])

  const { setMode } = useAvatarMode()

  const handleTargetChannelNotFound = useCallback(
    (_sponsors: SponsorInfo[]) => {
      // When a deeplink targets a media-mode channel the user doesn't belong to,
      // navigate directly to the public content library — no logout required since
      // unauth routes are accessible to authenticated users.
      if (
        autoSwitchChannelId &&
        deeplinkChannelInfo &&
        deeplinkChannelInfo.is_media_mode !== false
      ) {
        clearPendingChannelId()
        router.replace(`/unauth/channel-library/${autoSwitchChannelId}` as any)
        return
      }
      clearPendingChannelId()
      const channelName =
        deeplinkChannelInfo?.brand_title ?? deeplinkChannelInfo?.name ?? "requested"
      const subscriptionUrl = deeplinkChannelInfo?.subscription_url ?? null
      const message = `You do not currently have access to the ${channelName} channel.`
      if (subscriptionUrl) {
        Alert.alert("Channel Access", message, [
          {
            text: "Request Access",
            onPress: () => {
              openLinkInBrowser(subscriptionUrl)
              handleClose()
            },
          },
          { text: "Cancel", style: "cancel", onPress: handleClose },
        ])
      } else {
        Alert.alert("Channel Access", message, [{ text: "OK", onPress: handleClose }])
      }
    },
    [autoSwitchChannelId, clearPendingChannelId, deeplinkChannelInfo, handleClose, router],
  )

  const handleSwitchComplete = useCallback(() => {
    clearPendingChannelId()
    if (autoSwitchChannelId) {
      posthog?.capture("channel_link_entry_success", { channelId: autoSwitchChannelId })
    }
    setMode("large", false)
    // Use replace so we always land on index; router.back() can fail if sponsor switch
    // triggered layout re-render (Loading) and we're already at root.
    router.replace("/")
  }, [router, setMode, clearPendingChannelId, posthog, autoSwitchChannelId])

  const isDeeplinkSwitching = !!autoSwitchChannelId

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      {/* When deeplink switching, mount SelectSponsor off-screen so the auto-switch
          runs, while showing the Loading overlay to the user. */}
      {isDeeplinkSwitching && (
        <>
          <Loading message="Opening channel…" />
          <View style={styles.hidden}>
            <SelectSponsor
              targetChannelId={autoSwitchChannelId}
              onSwitchComplete={handleSwitchComplete}
              onSwitchError={handleSwitchError}
              onTargetChannelNotFound={handleTargetChannelNotFound}
              targetChannelMissingMessage="You do not have access to this channel."
              skipSafeAreaInsets
            />
          </View>
        </>
      )}

      {!isDeeplinkSwitching && (
        <SidekickView
          showAvatar={!hideAvatar && !isSessionDeactivated}
          autoShowAvatarOnSpeech={!hideAvatar}
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          showControlsBarBackground={true}
          darkBackground={true}
          style={themed($container)}
          onMenuPress={handleClose}
          riveOwner="video"
          controlsConfig={{ type: "none" }}
        >
          {/* Custom Header */}
          <View
            style={[
              themed($headerContainer),
              {
                top: insets.top,
                left: insets.left,
                right: insets.right,
              },
            ]}
            pointerEvents="box-none"
            onLayout={handleHeaderLayout}
          >
            <Text style={themed($headerTitle)}>{"Change Channel"}</Text>
            <CloseButton
              onPress={handleClose}
              style={{ position: "absolute", top: 22, right: 16 }}
            />
          </View>
          {/* Channel Selection Content - paddingBottom minimal so list extends to bar background */}
          <View
            style={[
              themed($contentWrapper),
              {
                paddingTop: headerHeight + (isLandscape ? 8 : 10),
                paddingBottom: isLandscape ? 0 : 16, // Small gap above bar; SidekickView reserves bar space via showControlsBarBackground
                paddingHorizontal: isLandscape && Platform.OS !== "web" ? 180 : 0, // Space for avatar overlay in native landscape
              },
            ]}
          >
            <SelectSponsor
              needsConfirmation
              shouldCloseConversation={shouldCloseConversation}
              closeConversation={handleCloseConversation}
              onSwitchStart={handleSwitchStart}
              onSwitchError={handleSwitchError}
              onSwitchComplete={handleSwitchComplete}
              onTargetChannelNotFound={handleTargetChannelNotFound}
              targetChannelId={autoSwitchChannelId}
              targetChannelMissingMessage="You do not have access to this channel."
              skipSafeAreaInsets
              style={styles.selectSponsor}
            />
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  selectSponsor: {
    marginTop: 0,
  },
  hidden: {
    position: "absolute",
    width: 0,
    height: 0,
    overflow: "hidden",
  },
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $contentWrapper: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})
